import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';
import { WebUiSelect } from '../../components/select';
import { useWebUiGlobalContext } from '../../context/global/use-global-context';
import DhcpRelaySubnetEditor from './dhcp-relay/dhcp-relay-subnet-editor';
import {
  type VlanMode,
  type VidSetting,
  type RelaySubnet,
  type RelaySettings,
  type RemoteIdSettings,
} from './dhcp-relay/types';

export default function WebUiSettingsDhcpRelayPage() {
  const { currentUser } = useWebUiGlobalContext();
  const readonly = currentUser?.userLevel === 1;
  const [hostname, setHostname] = React.useState('');
  const [remoteType, setRemoteType] = React.useState(0);
  const [remoteId, setRemoteId] = React.useState('');
  const [serverIdOverride, setServerIdOverride] = React.useState(false);
  const [vids, setVids] = React.useState<VidSetting[]>([]);
  const [subnets, setSubnets] = React.useState<RelaySubnet[]>([]);
  const [openedVid, setOpenedVid] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  const reload = React.useCallback(async () => {
    const [info, v, relayData, rid] = await Promise.all([
      postWebUiApi<{ name?: string }>({
        service: 'system',
        access: 'get',
        command: 'get info',
      }),
      postWebUiApi<VidSetting[]>({
        service: 'dhcp',
        access: 'get',
        command: 'get vids',
      }),
      postWebUiApi<RelaySettings>({
        service: 'dhcp',
        access: 'get',
        command: 'relay settings',
      }),
      postWebUiApi<RemoteIdSettings>({
        service: 'dhcp',
        access: 'get',
        command: 'remote id',
      }),
    ]);
    const nextHost = info.name ?? '';
    const nextType = Number(rid.remote_type ?? 0);
    const nextRemoteId =
      nextType === 1 ? nextHost : String(rid.remote_id ?? '');
    const nextSubnets = relayData.subnets ?? [];
    setHostname(nextHost);
    setVids(v ?? []);
    setRemoteType(nextType);
    setRemoteId(nextRemoteId);
    setServerIdOverride(Boolean(relayData.server_id_override));
    setSubnets(nextSubnets);
    setOpenedVid((prev) => {
      if (!nextSubnets.length) return null;
      if (prev != null && nextSubnets.some((s) => s.vid === prev)) return prev;
      return nextSubnets[0].vid;
    });
  }, []);

  React.useEffect(() => {
    reload().catch((err) => setError((err as Error).message));
  }, [reload]);

  const setSystemSettings = async () => {
    try {
      await Promise.all([
        postWebUiApi({
          service: 'dhcp',
          access: 'set',
          command: 'remote id',
          parameters: {
            remote_type: remoteType,
            remote_id: remoteType === 1 ? hostname : remoteId,
          },
        }),
        postWebUiApi({
          service: 'dhcp',
          access: 'set',
          command: 'server id override',
          parameters: { server_id_override: serverIdOverride },
        }),
      ]);
      setMessage('System settings updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const setVidSettings = async () => {
    try {
      await postWebUiApi({
        service: 'dhcp',
        access: 'set',
        command: 'set vids',
        parameters: vids,
      });
      setMessage('DHCP interface settings updated.');
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const updateRelaySubnet = async (vid: number) => {
    try {
      const subnet = subnets?.find((s) => s.vid === vid);
      await postWebUiApi({
        service: 'dhcp',
        access: 'set',
        command: 'relay update subnet',
        parameters: {
          vid,
          servers: subnet?.servers ?? [{ address: '', port: 67 }],
          ports: subnet?.ports ?? {},
        },
      });
      setMessage(
        `Relay settings updated for ${vid === 0 ? 'Lan' : `VLAN_ID${vid}`}.`,
      );
      setError(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <WebUiPageWrapper titleList={['Settings', 'DHCP Relay']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}
      <div className="flex w-full flex-row gap-2">
        <div className="flex w-1/3 flex-col gap-2">
          <h4 className="font-bold">DHCP System Settings</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Name</th>
                <th>Setting</th>
              </tr>
            </thead>
            <tbody>
              <tr className="webui-table-row-content">
                <td>
                  Remote ID Type <br />
                  (shared with DHCP Inserter)
                </td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'Manual', value: '0' },
                      { label: 'Hostname', value: '1' },
                    ]}
                    disabled={readonly}
                    value={remoteType.toString()}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setRemoteType(next);
                      if (next === 1) setRemoteId(hostname);
                    }}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Remote ID (shared with DHCP Inserter)</td>
                <td>
                  <WebUiInput
                    inputSize="sm"
                    className="w-full"
                    disabled={readonly || remoteType === 1}
                    value={remoteType === 1 ? hostname : remoteId}
                    onChange={(e) => setRemoteId(e.target.value)}
                  />
                </td>
              </tr>
              <tr className="webui-table-row-content">
                <td>Server ID Override</td>
                <td>
                  <WebUiSelect
                    inputSize="sm"
                    className="w-full"
                    options={[
                      { label: 'Enabled', value: 'true' },
                      { label: 'Disabled', value: 'false' },
                    ]}
                    disabled={readonly}
                    value={serverIdOverride.toString()}
                    onChange={(e) =>
                      setServerIdOverride(e.target.value === 'true')
                    }
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <div>
            <WebUiButton disabled={readonly} onClick={setSystemSettings}>
              Set System Settings
            </WebUiButton>
          </div>
          <h4 className="mt-4 font-bold">DHCP per Management Interfaces</h4>
          <table className="webui-table w-full">
            <thead>
              <tr className="webui-table-row-head">
                <th>Interface</th>
                <th>Status DHCP</th>
              </tr>
            </thead>
            <tbody>
              {vids.map((v) => (
                <tr key={v.vid} className="webui-table-row-content">
                  <td>{v.vid === 0 ? 'Lan' : `VLAN_ID${v.vid}`}</td>
                  <td>
                    <WebUiSelect
                      inputSize="sm"
                      className="w-full"
                      options={[
                        { label: 'Server', value: 'server' },
                        { label: 'Relay', value: 'relay' },
                        { label: 'Inserter', value: 'inserter' },
                        { label: 'Off', value: 'off' },
                      ]}
                      disabled={readonly}
                      value={v.type.toString()}
                      onChange={(e) =>
                        setVids((prev) =>
                          prev.map((x) =>
                            x.vid === v.vid
                              ? { ...x, type: e.target.value as VlanMode }
                              : x,
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <WebUiButton disabled={readonly} onClick={setVidSettings}>
              Set DHCP Interface Settings
            </WebUiButton>
          </div>
        </div>
        <div className="flex w-2/3 flex-col gap-2">
          <h4 className="font-bold">Subnet</h4>
          <DhcpRelaySubnetEditor
            readonly={readonly}
            subnets={subnets}
            openedVid={openedVid}
            setOpenedVid={setOpenedVid}
            setSubnets={setSubnets}
            onSaveSubnet={updateRelaySubnet}
          />
          <div>
            <WebUiButton
              onClick={() =>
                reload().catch((err) => setError((err as Error).message))
              }
            >
              Reload
            </WebUiButton>
          </div>
        </div>
      </div>
    </WebUiPageWrapper>
  );
}
